import React, { useState } from 'react';
import { toast } from 'sonner';
import { businesses } from '../mockData';
import { submitContact } from '../lib/api';

/**
 * ContactForm — reusable, minimalist, MNC-grade contact form.
 * Real submission to POST /api/contacts via lib/api.
 *
 * Props:
 *   context: 'general' | business.id (adds business-specific field)
 *   business: optional business object (for context-aware labels)
 */
const ContactForm = ({ context = 'general', business = null, className = '' }) => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '',
    business: business?.id || '', serviceType: '', propertyType: '',
    budget: '', message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const onChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    if (fieldErrors[e.target.name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[e.target.name];
        return next;
      });
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setFieldErrors({});
    try {
      const payload = {
        ...form,
        source: business ? `business:${business.id}` : 'contact-page',
        business: form.business || (business ? business.id : ''),
      };
      await submitContact(payload);
      setSubmitted(true);
      toast.success('Message received. Our team will respond within one business day.');
      setForm({
        name: '', email: '', phone: '', company: '',
        business: business?.id || '', serviceType: '', propertyType: '',
        budget: '', message: ''
      });
      setTimeout(() => setSubmitted(false), 6000);
    } catch (err) {
      if (err.status === 400 && Array.isArray(err.details) && err.details.length) {
        const errMap = {};
        err.details.forEach((d) => { if (d.field) errMap[d.field] = d.message; });
        setFieldErrors(errMap);
        toast.error('Please correct the highlighted fields.');
      } else if (err.status === 429) {
        toast.error('Too many submissions. Please try again in a few minutes.');
      } else {
        toast.error(`Unable to send: ${err.message}. Please email ${business?.email || 'info@lockene.info'}.`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    'w-full bg-transparent border-0 border-b border-brand-hairline focus:border-brand-oxford outline-none py-3 text-brand-oxford placeholder:text-brand-slate/50 text-[15px] transition-colors';
  const labelCls =
    'block text-[11px] tracking-[0.16em] uppercase font-bold text-brand-slate mb-2';

  const inputClsFor = (name) =>
    `${inputCls} ${fieldErrors[name] ? 'border-red-600 focus:border-red-600' : ''}`;

  const FieldError = ({ name }) =>
    fieldErrors[name] ? (
      <p className="mt-1 text-xs text-red-600" data-testid={`err-${name}`}>{fieldErrors[name]}</p>
    ) : null;

  return (
    <form
      onSubmit={onSubmit}
      className={`bg-white border border-brand-hairline p-8 md:p-12 ${className}`}
      data-testid="contact-form"
      noValidate
    >
      {submitted && (
        <div
          className="mb-8 p-4 border border-brand-oxford bg-brand-alabaster flex items-start gap-3"
          role="status"
          data-testid="contact-success"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-brand-oxford mt-0.5">
            <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <div>
            <p className="text-sm font-semibold text-brand-oxford">Message received</p>
            <p className="text-sm text-brand-slate">A senior team member will respond within one business day.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
        <div>
          <label htmlFor="cf-name" className={labelCls}>Full name *</label>
          <input id="cf-name" name="name" required value={form.name} onChange={onChange} placeholder="Jane Doe" className={inputClsFor('name')} data-testid="cf-name" />
          <FieldError name="name" />
        </div>
        <div>
          <label htmlFor="cf-email" className={labelCls}>Work email *</label>
          <input id="cf-email" type="email" name="email" required value={form.email} onChange={onChange} placeholder="you@company.com" className={inputClsFor('email')} data-testid="cf-email" />
          <FieldError name="email" />
        </div>
        <div>
          <label htmlFor="cf-phone" className={labelCls}>Phone</label>
          <input id="cf-phone" type="tel" name="phone" value={form.phone} onChange={onChange} placeholder="+1 234 567 8900" className={inputClsFor('phone')} data-testid="cf-phone" />
          <FieldError name="phone" />
        </div>
        <div>
          <label htmlFor="cf-company" className={labelCls}>Company / Organisation</label>
          <input id="cf-company" name="company" value={form.company} onChange={onChange} placeholder="Company Ltd." className={inputCls} data-testid="cf-company" />
        </div>

        {context === 'general' && (
          <div className="md:col-span-2">
            <label htmlFor="cf-business" className={labelCls}>Interested in</label>
            <select id="cf-business" name="business" value={form.business} onChange={onChange} className={`${inputCls} appearance-none pr-8`} data-testid="cf-business">
              <option value="">Select a business unit (optional)</option>
              {businesses.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
        )}

        {business?.id === 'lockene-compliances' && (
          <div className="md:col-span-2">
            <label htmlFor="cf-service" className={labelCls}>Certification of interest</label>
            <input id="cf-service" name="serviceType" value={form.serviceType} onChange={onChange} placeholder="e.g. BIS, ISI, WPC, CDSCO" className={inputCls} data-testid="cf-service" />
          </div>
        )}

        {business?.id === 'locacres' && (
          <>
            <div>
              <label htmlFor="cf-property" className={labelCls}>Property type</label>
              <input id="cf-property" name="propertyType" value={form.propertyType} onChange={onChange} placeholder="Villa, apartment, commercial…" className={inputCls} data-testid="cf-property" />
            </div>
            <div>
              <label htmlFor="cf-budget" className={labelCls}>Budget range</label>
              <input id="cf-budget" name="budget" value={form.budget} onChange={onChange} placeholder="USD / INR range" className={inputCls} data-testid="cf-budget" />
            </div>
          </>
        )}

        {business?.id === 'styx-cafe' && (
          <div className="md:col-span-2">
            <label htmlFor="cf-service" className={labelCls}>Inquiry type</label>
            <input id="cf-service" name="serviceType" value={form.serviceType} onChange={onChange} placeholder="Franchise, visit, partnership…" className={inputCls} data-testid="cf-service" />
          </div>
        )}

        <div className="md:col-span-2">
          <label htmlFor="cf-message" className={labelCls}>How can we help? *</label>
          <textarea id="cf-message" name="message" required rows={5} value={form.message} onChange={onChange} placeholder="Briefly describe your requirement, timelines and any relevant context." className={`${inputClsFor('message')} resize-none`} data-testid="cf-message" />
          <FieldError name="message" />
        </div>
      </div>

      <div className="mt-10 flex items-center justify-between flex-wrap gap-4">
        <p className="text-xs text-brand-slate max-w-md">
          By submitting this form you agree to be contacted by a Lockene representative. We do not share your data.
        </p>
        <button
          type="submit"
          disabled={submitting}
          data-testid="cf-submit"
          className="inline-flex items-center gap-3 bg-brand-oxford text-brand-alabaster px-7 py-4 text-[12px] font-semibold tracking-[0.16em] uppercase disabled:opacity-50 disabled:cursor-not-allowed transition-colors hover:bg-brand-gold hover:text-brand-oxford focus-ring"
        >
          {submitting ? 'Sending…' : 'Send Message'}
          {!submitting && <span aria-hidden>→</span>}
        </button>
      </div>
    </form>
  );
};

export default ContactForm;
